import React from 'react';
import { Text, tokens } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { useConfirmationStyles } from './Confirmation.styles';
import { ThankYouCondensedSummary } from './ThankYouCondensedSummary';
import type { SpaceDetails } from '@store/models/booking';
import type { Building } from '@store/models/buildings/entities';
import { getActiveAccount } from '@/services';

const SuccessIcon: React.FC = () => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M26.2289 5.42394C26.529 5.52602 26.8221 5.64744 27.1064 5.78741L30.5272 7.47154C31.4555 7.92861 32.5435 7.92861 33.4717 7.47154L36.8925 5.78741C40.5259 3.99845 44.9219 5.49381 46.7107 9.12736L46.9067 9.56024L47.0741 10.0049L48.3021 13.6145C48.6355 14.5941 49.4048 15.3635 50.3843 15.6967L53.9939 16.9246C57.8283 18.2289 59.8792 22.3946 58.5749 26.2289C58.4728 26.529 58.3515 26.8221 58.2115 27.1064L56.5272 30.5272C56.0701 31.4555 56.0701 32.5435 56.5272 33.4717L58.2115 36.8925C60.0005 40.5259 58.5051 44.9219 54.8715 46.7107C54.5872 46.8507 54.2941 46.9723 53.9939 47.0741L50.3843 48.3021C49.4048 48.6355 48.6355 49.4048 48.3021 50.3843L47.0741 53.9939C45.7699 57.8283 41.6043 59.8792 37.7699 58.5749C37.4699 58.4728 37.1768 58.3515 36.8925 58.2115L33.4717 56.5272C32.5435 56.0701 31.4555 56.0701 30.5272 56.5272L27.1064 58.2115C23.4729 60.0005 19.077 58.5051 17.2881 54.8715C17.1481 54.5872 17.0267 54.2941 16.9246 53.9939L15.6967 50.3843C15.3635 49.4048 14.5941 48.6355 13.6145 48.3021L10.0049 47.0741C6.17056 45.7699 4.11962 41.6043 5.42394 37.7699C5.52602 37.4699 5.64744 37.1768 5.78741 36.8925L7.47154 33.4717C7.92861 32.5435 7.92861 31.4555 7.47154 30.5272L5.78741 27.1064C3.99845 23.4729 5.49381 19.077 9.12736 17.2881C9.4117 17.1481 9.70482 17.0267 10.0049 16.9246L13.6145 15.6967C14.5941 15.3635 15.3635 14.5941 15.6967 13.6145L16.9246 10.0049C18.2289 6.17056 22.3946 4.11962 26.2289 5.42394ZM41.252 23.9186L26.8005 38.3699L21.5359 32.0525C20.8287 31.2037 19.5676 31.0891 18.7191 31.7963C17.8705 32.5035 17.7559 33.7645 18.463 34.6131L25.1297 42.6131C25.8826 43.5165 27.2488 43.5787 28.0803 42.7469L44.0803 26.7469C44.8613 25.9659 44.8613 24.6996 44.0803 23.9186C43.2992 23.1375 42.0331 23.1375 41.252 23.9186Z"
      fill={tokens.colorPaletteGreenForeground1}
    />
  </svg>
);

export interface ConfirmationProps {
  onBookAnother: () => void;
  onGoHome: () => void;
  /** Optional submitted form data; when provided, a condensed summary renders beneath the actions. */
  formData?: Record<string, unknown>;
  selectedSpace?: SpaceDetails | null;
  selectedBuilding?: Building | null;
  /** Submitted request reference id — rendered as the first row of the condensed summary. */
  requestId?: string | null;
}

export const Confirmation: React.FC<ConfirmationProps> = ({
  onBookAnother,
  onGoHome,
  formData,
  selectedSpace,
  selectedBuilding,
  requestId,
}) => {
  const { t } = useTranslation('bookingForm');
  const styles = useConfirmationStyles();
  const userEmail = getActiveAccount()?.username ?? '';

  return (
    <div className={styles.root}>
      <SuccessIcon />
      <Text as="h1" className={styles.title}>
        {t('bookingForm.confirmation.title')}
      </Text>
      <Text className={styles.message}>
        {t('bookingForm.confirmation.message', { email: userEmail })}
      </Text>
      <div className={styles.actions}>
        <BasicButton variant="outline" size="medium" onClick={onBookAnother}>
          {t('bookingForm.buttons.bookAnotherSpace')}
        </BasicButton>
        <BasicButton variant="primary" size="medium" onClick={onGoHome}>
          {t('bookingForm.buttons.home')}
        </BasicButton>
      </div>
      {formData && (
        <ThankYouCondensedSummary
          formData={formData}
          selectedSpace={selectedSpace ?? null}
          selectedBuilding={selectedBuilding ?? null}
          requestId={requestId ?? null}
        />
      )}
    </div>
  );
};

Confirmation.displayName = 'Confirmation';
